import { Grid } from '../../core/Grid.js';
import {
    Direction,
    Position,
    Point,
    pointKey,
    pointsEqual,
    positionKey,
    parsePositionKey,
    isRimEdge,
} from '../../core/GridGeometry.js';
import { ScoreBreakdown, calculateScore } from '../../core/Scoring.js';
import { LineGame, TraceResult, ViolationKind, segmentKey, parseSegmentKey } from './LineGame.js';
import { VortexGateManager } from '../VortexGateManager.js';
import { IVortexGame } from '../IVortexGame.js';

export class LineVortex extends LineGame implements IVortexGame {
    private readonly gm = new VortexGateManager();

    // Rim-vortex violations: segment keys that can't be mirrored (mirror would be rim edge)
    private rimVortexSegments: Set<string> = new Set();

    // Gate-jumped flags (reset when path leaves gate pair)
    private gateJumpedFlags: Set<string> = new Set(); // canonical pair IDs

    constructor(grid: Grid) {
        super(grid);
    }

    // ── Vortex Data ───────────────────────────────────────────────

    setVortexData(gatePairs: Array<[Position, Position]>, allGates: Set<string>): void {
        this.gm.setVortexData(gatePairs, allGates, () => {
            this.rebuildRimVortexFlags();
        });
    }

    // ── Gate Queries ──────────────────────────────────────────────

    isGateCell(pos: Position): boolean {
        return this.gm.isGateCell(pos);
    }

    isActiveGate(pos: Position): boolean {
        return this.gm.isActiveGate(pos);
    }

    canActivateGate(pos: Position): boolean {
        return this.gm.canActivateGate(pos, (p, pairedPos) => {
            if (this.getGrid().hasDigitAt(p)) return false;
            if (this.getGrid().hasDigitAt(pairedPos)) return false;
            return true;
        });
    }

    toggleVortexGate(pos: Position): boolean {
        const key = positionKey(pos);

        if (this.gm.raw.has(key)) {
            const pairedKey = this.gm.raw.get(key)!;
            this.gm.raw.delete(key);
            this.gm.raw.delete(pairedKey);
            this.rebuildRimVortexFlags();
            return true;
        }

        if (this.canActivateGate(pos)) {
            const pair = this.gm.findPair(key);
            if (pair) {
                this.gm.raw.set(key, pair.pairedKey);
                this.gm.raw.set(pair.pairedKey, key);
                // Auto-mirror existing segments on newly activated gates
                const pairedPos = parsePositionKey(pair.pairedKey);
                this.mirrorExistingSegments(pos, pairedPos);
                this.rebuildRimVortexFlags();
                return true;
            }
        }
        return false;
    }

    getActiveGatePairs(): Map<string, string> {
        return this.gm.getActiveGatePairs();
    }

    getVortexGatePairs(): Array<[Position, Position]> {
        return this.gm.getVortexGatePairs();
    }

    getAllGatePositions(): Set<string> {
        return this.gm.getAllGatePositions();
    }

    // ── Gate Geometry Helpers ─────────────────────────────────────

    /** Check if a point is a corner of the given cell */
    private isCornerOf(p: Point, cellPos: Position): boolean {
        const dr = p.row - cellPos.row;
        const dc = p.col - cellPos.col;
        return (dr === 0 || dr === 1) && (dc === 0 || dc === 1);
    }

    /** Map a corner from one cell to the corresponding corner of another */
    private mapCorner(p: Point, fromCell: Position, toCell: Position): Point {
        return { row: toCell.row + (p.row - fromCell.row), col: toCell.col + (p.col - fromCell.col) };
    }

    /** Get all gate pairs that have this point as a corner */
    private findAllGatesForCorner(p: Point): Array<{ gateCell: Position; pairedCell: Position; pairId: string }> {
        const results: Array<{ gateCell: Position; pairedCell: Position; pairId: string }> = [];
        const processed = new Set<string>();
        for (const [keyA, keyB] of this.gm.raw) {
            const pairId = keyA < keyB ? `${keyA}|${keyB}` : `${keyB}|${keyA}`;
            if (processed.has(pairId)) continue;
            processed.add(pairId);

            const posA = parsePositionKey(keyA);
            const posB = parsePositionKey(keyB);
            if (this.isCornerOf(p, posA)) results.push({ gateCell: posA, pairedCell: posB, pairId });
            else if (this.isCornerOf(p, posB)) results.push({ gateCell: posB, pairedCell: posA, pairId });
        }
        return results;
    }

    // ── Segment Mirroring ─────────────────────────────────────────

    override toggleSegment(from: Point, to: Point): boolean {
        const result = super.toggleSegment(from, to);
        if (!result) return false;

        this.mirrorIfGateEdge(from, to);
        this.rebuildRimVortexFlags();
        return true;
    }

    private mirrorIfGateEdge(from: Point, to: Point): void {
        if (this.gm.raw.size === 0) return;

        const processed = new Set<string>();
        for (const [keyA, keyB] of this.gm.raw) {
            const pairId = [keyA, keyB].sort().join('|');
            if (processed.has(pairId)) continue;
            processed.add(pairId);

            const posA = parsePositionKey(keyA);
            const posB = parsePositionKey(keyB);

            if (this.isCornerOf(from, posA) && this.isCornerOf(to, posA)) {
                this.syncMirror(from, to, posA, posB);
                return;
            }
            if (this.isCornerOf(from, posB) && this.isCornerOf(to, posB)) {
                this.syncMirror(from, to, posB, posA);
                return;
            }
        }
    }

    private syncMirror(from: Point, to: Point, fromCell: Position, toCell: Position): void {
        const mirrorFrom = this.mapCorner(from, fromCell, toCell);
        const mirrorTo = this.mapCorner(to, fromCell, toCell);

        // Check if mirror would be a rim edge
        if (this.wouldBeRimEdge(mirrorFrom, mirrorTo)) {
            // Don't place mirror; original gets flagged as rim-vortex (handled in rebuildRimVortexFlags)
            return;
        }

        const mirrorKey = segmentKey(mirrorFrom, mirrorTo);
        const origKey = segmentKey(from, to);
        if (this.segments.has(origKey) && !this.segments.has(mirrorKey)) {
            super.toggleSegment(mirrorFrom, mirrorTo);
        } else if (!this.segments.has(origKey) && this.segments.has(mirrorKey)) {
            super.toggleSegment(mirrorFrom, mirrorTo);
        }
    }

    /** Check if a segment between two points would be a rim edge */
    private wouldBeRimEdge(from: Point, to: Point): boolean {
        if (this.grid.isRimPoint(from) && this.grid.isRimPoint(to)) {
            const edge = this.grid.createEdge(from, to);
            if (edge && isRimEdge(edge)) return true;
        }
        return false;
    }

    /** Mirror all existing gate-internal segments when a gate pair is activated */
    private mirrorExistingSegments(posA: Position, posB: Position): void {
        // Find all segments that are edges of cell A, mirror to B
        for (const segKey of [...this.segments]) {
            const [p1, p2] = parseSegmentKey(segKey);
            if (this.isCornerOf(p1, posA) && this.isCornerOf(p2, posA)) {
                this.syncMirror(p1, p2, posA, posB);
            } else if (this.isCornerOf(p1, posB) && this.isCornerOf(p2, posB)) {
                this.syncMirror(p1, p2, posB, posA);
            }
        }
    }

    /** Rebuild the set of rim-vortex violation segment keys */
    private rebuildRimVortexFlags(): void {
        this.rimVortexSegments.clear();

        const processed = new Set<string>();
        for (const [keyA, keyB] of this.gm.raw) {
            const pairId = [keyA, keyB].sort().join('|');
            if (processed.has(pairId)) continue;
            processed.add(pairId);

            const posA = parsePositionKey(keyA);
            const posB = parsePositionKey(keyB);

            for (const segKey of this.segments) {
                const [p1, p2] = parseSegmentKey(segKey);
                // Check if segment is an edge of cell A whose mirror at B would be rim
                if (this.isCornerOf(p1, posA) && this.isCornerOf(p2, posA)) {
                    const mf = this.mapCorner(p1, posA, posB);
                    const mt = this.mapCorner(p2, posA, posB);
                    if (this.wouldBeRimEdge(mf, mt)) {
                        this.rimVortexSegments.add(segKey);
                    }
                }
                // Check if segment is an edge of cell B whose mirror at A would be rim
                if (this.isCornerOf(p1, posB) && this.isCornerOf(p2, posB)) {
                    const mf = this.mapCorner(p1, posB, posA);
                    const mt = this.mapCorner(p2, posB, posA);
                    if (this.wouldBeRimEdge(mf, mt)) {
                        this.rimVortexSegments.add(segKey);
                    }
                }
            }
        }
    }

    // ── Gate-aware Trace ──────────────────────────────────────────

    protected override resetGateFlags(tip: Point): void {
        // For each gate pair: if tip is NOT a corner of either cell, reset the jumped flag
        const processed = new Set<string>();
        for (const [keyA, keyB] of this.gm.raw) {
            const pairId = keyA < keyB ? `${keyA}|${keyB}` : `${keyB}|${keyA}`;
            if (processed.has(pairId)) continue;
            processed.add(pairId);

            const posA = parsePositionKey(keyA);
            const posB = parsePositionKey(keyB);
            if (!this.isCornerOf(tip, posA) && !this.isCornerOf(tip, posB)) {
                this.gateJumpedFlags.delete(pairId);
            }
        }
    }

    protected override checkGateJump(
        tip: Point,
        _lastDirection: Direction | null,
        _index: unknown,
        _usedSegments: Set<string>,
    ): { exitCorner: Point; violation?: ViolationKind; violationSegKey?: string } | null {
        if (this.gm.raw.size === 0) return null;

        // Find all possible gate jumps from this corner
        const possibleJumps: Array<{ exitCorner: Point; pairId: string }> = [];

        const gates = this.findAllGatesForCorner(tip);
        for (const { gateCell, pairedCell, pairId } of gates) {
            if (this.gateJumpedFlags.has(pairId)) continue;
            const exitCorner = this.mapCorner(tip, gateCell, pairedCell);
            possibleJumps.push({ exitCorner, pairId });
        }

        if (possibleJumps.length === 0) return null;

        if (possibleJumps.length > 1) {
            return { exitCorner: tip, violation: 'branching-jump' };
        }

        const jump = possibleJumps[0];

        // Successful jump
        this.gateJumpedFlags.add(jump.pairId);
        return { exitCorner: jump.exitCorner };
    }

    protected override checkMirroredTargetHits(
        segKey: string,
        hitCornersPerDigit: Map<number, Set<string>>,
        lastCornerPerDigit: Map<number, Point>,
        hitCorners: Set<string>,
        clearedDigits: number[],
    ): void {
        const mirror = this.findMirrorSegment(segKey);
        if (!mirror || !this.segments.has(mirror)) return;

        const [p1, p2] = parseSegmentKey(mirror);
        for (const p of [p1, p2]) {
            const pk = pointKey(p);
            for (const [digit, target] of this.getDigitTargets()) {
                for (const corner of target.corners) {
                    if (pointsEqual(corner, p)) {
                        hitCorners.add(pk);
                        hitCornersPerDigit.get(digit)!.add(pk);
                        lastCornerPerDigit.set(digit, { ...p });

                        const hitCount = hitCornersPerDigit.get(digit)!.size;
                        if (hitCount === target.corners.length && !clearedDigits.includes(digit)) {
                            if (clearedDigits.length === digit - this.getGrid().minDigit) {
                                clearedDigits.push(digit);
                            }
                        }
                    }
                }
            }
        }
    }

    // Override trace to add rim-vortex violations and handle gate segment deduplication
    override trace(): TraceResult {
        // Reset gate-jumped flags before each trace
        this.gateJumpedFlags.clear();

        const result = super.trace();

        // Add rim-vortex violations
        for (const segKey of this.rimVortexSegments) {
            if (result.tracedSegmentKeys.has(segKey)) {
                result.violationKeys.add(segKey);
                if (!result.violationType) result.violationType = 'rim-vortex';
            }
        }

        // Collect mirrored gate segments (traced segments whose mirrors exist but aren't traced)
        for (const segKey of [...result.tracedSegmentKeys]) {
            const mirror = this.findMirrorSegment(segKey);
            if (mirror && this.segments.has(mirror) && !result.tracedSegmentKeys.has(mirror)) {
                result.mirroredSegmentKeys.add(mirror);
            }
        }

        // Re-settle the win: the mirrors above, and any rim-vortex violation added,
        // are placed segments that must not count as stranded.
        return this.finalize(result);
    }

    /** Find the mirror segment key for a gate-internal edge */
    private findMirrorSegment(segKey: string): string | null {
        const [p1, p2] = parseSegmentKey(segKey);
        const processed = new Set<string>();
        for (const [keyA, keyB] of this.gm.raw) {
            const pairId = [keyA, keyB].sort().join('|');
            if (processed.has(pairId)) continue;
            processed.add(pairId);

            const posA = parsePositionKey(keyA);
            const posB = parsePositionKey(keyB);

            if (this.isCornerOf(p1, posA) && this.isCornerOf(p2, posA)) {
                return segmentKey(this.mapCorner(p1, posA, posB), this.mapCorner(p2, posA, posB));
            }
            if (this.isCornerOf(p1, posB) && this.isCornerOf(p2, posB)) {
                return segmentKey(this.mapCorner(p1, posB, posA), this.mapCorner(p2, posB, posA));
            }
        }
        return null;
    }

    // ── Score Override ─────────────────────────────────────────────

    override getScore(): ScoreBreakdown | null {
        const result = this.trace();
        if (!result.won) return null;

        // Deduplicate mirrored gate edges for segment count
        const counted = new Set<string>();
        let segCount = 0;
        for (const segKey of this.segments) {
            const mirror = this.findMirrorSegment(segKey);
            const canonical = mirror && mirror < segKey ? mirror : segKey;
            if (!counted.has(canonical)) {
                counted.add(canonical);
                segCount++;
            }
        }

        return calculateScore(result.cornerCount, segCount);
    }

    // ── State Management ──────────────────────────────────────────

    override reset(): void {
        super.reset();
        this.gm.clearActiveGates();
        this.rimVortexSegments.clear();
        this.gateJumpedFlags.clear();
    }

    override isStarted(): boolean {
        return super.isStarted() || this.gm.hasActiveGates();
    }

    // ── Serialization ─────────────────────────────────────────────

    override serialize(): string {
        return this.gm.serializeWithBase(super.serialize(), this.gs);
    }

    override deserialize(serialized: string): void {
        this.gm.clearActiveGates();
        this.rimVortexSegments.clear();
        this.gateJumpedFlags.clear();

        if (!serialized) {
            super.deserialize('');
            return;
        }

        const { basePart, gatePart } = VortexGateManager.splitSerialized(serialized);

        super.deserialize(basePart);
        this.gm.deserializeGates(gatePart, this.gs);

        this.rebuildRimVortexFlags();
    }
}
